import * as Sentry from "@sentry/browser";
import { Spin, message } from "antd";
import { FormInstance } from "antd/lib/form/Form";
import React from "react";
import BaseForm from "../../components/Form/BaseForm";
import StepContainer from "../../components/StepContainer";

import {
  APIError,
  FormFieldConfig,
  Opportunity,
  SelectOptionsInterface,
} from "../../helpers/shared-interfaces";

import { AxiosResponse } from "axios";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../../helpers/utilities";
import { ASSIGN_OPPORTUNITY_SOURCE_TO_OPPORTUNITY } from "../../gql/opportunity";

interface Props {
  opportunitySource?: string;
  opportunitySources: SelectOptionsInterface[] | [];
  opportunity: Opportunity;
  next: () => void;
  setPercentage: (percentage: number) => void;
  getNextViewName: () => string | null;
  getPreviousViewName: () => string | null;
}

interface State {
  isLoading: boolean;
}

export default class OpportunitySource extends React.Component<Props, State> {
  reference = React.createRef<FormInstance>();

  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      isLoading: true,
    };

    this.assignOpportunitySourceToOpportunity =
      this.assignOpportunitySourceToOpportunity.bind(this);
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  componentDidMount(): void {
    this.props.setPercentage(0);
    this.setState({ isLoading: false });
  }

  assignOpportunitySourceToOpportunity<T>(
    opportunitySource: string,
  ): Promise<AxiosResponse<T>> {
    const { opportunity } = this.props;
    return request({
      query: ASSIGN_OPPORTUNITY_SOURCE_TO_OPPORTUNITY,
      variables: {
        opportunityId: opportunity.id,
        opportunitySourceId: opportunitySource,
      },
    });
  }

  next = async (): Promise<void> => {
    interface Response {
      data?: {
        assignOpportunitySourceToOpportunity?: {
          opportunity?: Opportunity;
          errors: APIError[];
        };
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(["opportunitySource"]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    const assignMutation = this.assignOpportunitySourceToOpportunity;

    try {
      const response: AxiosResponse<Response> = await assignMutation<Response>(
        data.opportunitySource,
      );

      const { err, handled } = responseHandler<Response>(
        response,
        "assignOpportunitySourceToOpportunity",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        this.props.next();
      }
    } catch (error) {
      this.setState({ isLoading: false });
      message.error(`Request Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  render(): JSX.Element {
    const { opportunitySources, opportunitySource } = this.props;
    const { isLoading } = this.state;

    const fields: FormFieldConfig[] = [
      {
        name: "opportunitySource",
        inputType: "select",
        rule: "string",
        label: "Opportunity Source",
        placeholder: "Opportunity Source",
        required: true,
        colConfig: {
          span: 8,
        },

        initialValue: opportunitySource,
        selectOptions: opportunitySources,
      },
    ];

    return (
      <Spin spinning={isLoading}>
        <StepContainer
          getNextViewName={this.props.getNextViewName}
          getPreviousViewName={this.props.getPreviousViewName}
          style={{
            position: "relative",
            top: "-10px",
          }}
          config={{
            next: this.next,
            showNext: true,
            showPrev: false,
            showCancelEdit: false
          }}
        >
          <BaseForm
            layout="vertical"
            reference={this.reference}
            fieldsToHide={[]}
            fields={fields}
          ></BaseForm>
        </StepContainer>
      </Spin>
    );
  }
}
